import { UserCheck, Briefcase, Shield } from "lucide-react";
import { Link } from "react-router-dom";
import ServicePageLayout from "../components/ServicePageLayout";

const prestations = [
    {
        icon: UserCheck,
        title: "Mise à disposition d'administrateur",
        desc: "Pour les SA, mise à disposition d'un administrateur domicilié en Suisse, conformément aux exigences du Code des obligations.",
    },
    {
        icon: Briefcase,
        title: "Mise à disposition de gérant",
        desc: "Pour les Sàrl, mise à disposition d'un gérant résident en Suisse, garant de la représentation légale de la société.",
    },
    {
        icon: Shield,
        title: "Suivi et conformité",
        desc: "Fiduciaire Bleich assure un suivi régulier des obligations légales, des convocations d'assemblées et de la tenue des registres.",
    },
];

const etapes = [
    {
        num: "01",
        title: "Entretien préalable",
        desc: "Pour comprendre votre société et le périmètre du mandat.",
    },
    {
        num: "02",
        title: "Mise en place de la représentation",
        desc: "Inscription au registre du commerce et organisation du mandat.",
    },
    {
        num: "03",
        title: "Suivi continu",
        desc: "Suivi des obligations légales et reporting régulier aux actionnaires.",
    },
];

const pourquoiCols = [
    {
        title: "Expérience internationale",
        desc: "Fiduciaire Bleich accompagne régulièrement des actionnaires étrangers qui ont besoin d'une représentation suisse de confiance pour leur société.",
    },
    {
        title: "Discrétion et rigueur",
        desc: "Chaque mandat est traité avec la plus grande confidentialité, dans le strict respect des intérêts des actionnaires et des obligations légales.",
    },
    {
        title: "Une fiduciaire, pas un intermédiaire",
        desc: (
            <>
                Fiduciaire Bleich gère aussi la{" "}
                <Link
                    to="/comptabilite"
                    data-testid="link-comptabilite"
                    className="text-[#1C9CAC] hover:underline"
                >
                    comptabilité
                </Link>
                , la{" "}
                <Link
                    to="/fiscalite"
                    data-testid="link-fiscalite"
                    className="text-[#1C9CAC] hover:underline"
                >
                    fiscalité
                </Link>{" "}
                et les{" "}
                <Link
                    to="/salaires-rh"
                    data-testid="link-salaires-rh"
                    className="text-[#1C9CAC] hover:underline"
                >
                    salaires
                </Link>{" "}
                de ces sociétés. Tout est centralisé, rien n'est externalisé.
            </>
        ),
    },
];

const faqs = [
    {
        q: "Une société suisse doit-elle avoir un représentant domicilié en Suisse ?",
        a: "Oui. Une SA doit pouvoir être représentée par au moins une personne domiciliée en Suisse, et il en va de même pour le gérant d'une Sàrl. Fiduciaire Bleich met à disposition cette personne qualifiée.",
    },
    {
        q: "Un actionnaire étranger peut-il détenir la totalité d'une société suisse ?",
        a: "La condition légale porte sur la représentation locale, pas sur la nationalité ou le lieu de résidence des actionnaires. Un actionnariat entièrement étranger est donc possible avec un représentant en Suisse, rôle assuré par Fiduciaire Bleich.",
    },
    {
        q: "Quelle est la responsabilité d'un administrateur ou d'un gérant ?",
        a: "L'administrateur ou le gérant assume des responsabilités légales envers la société, les actionnaires et les tiers. Fiduciaire Bleich exerce ce rôle avec rigueur et dans le respect du cadre légal.",
    },
    {
        q: "En quoi consiste concrètement le mandat de Fiduciaire Bleich ?",
        a: "Fiduciaire Bleich assure la représentation légale, le suivi des obligations, la convocation des assemblées et la tenue des registres, en lien étroit avec les actionnaires.",
    },
];

const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Administrateur et gérant à Genève",
    serviceType: "Mise à disposition d'administrateur ou de gérant domicilié en Suisse",
    provider: {
        "@type": "AccountingService",
        name: "Fiduciaire Bleich Sàrl",
        url: "https://fiduciaire-bleich.ch",
    },
    areaServed: { "@type": "AdministrativeArea", name: "Canton de Genève" },
    url: "https://fiduciaire-bleich.ch/administrateur-gerant",
};

const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: "https://fiduciaire-bleich.ch/" },
        { "@type": "ListItem", position: 2, name: "Nos services", item: "https://fiduciaire-bleich.ch/" },
        { "@type": "ListItem", position: 3, name: "Administrateur et gérant", item: "https://fiduciaire-bleich.ch/administrateur-gerant" },
    ],
};

const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
};

const AdministrateurGerant = () => {
    return (
        <ServicePageLayout
            testIdPrefix="administrateur-gerant"
            seo={{
                title: "Administrateur et gérant à Genève | Fiduciaire Bleich",
                description:
                    "Mise à disposition d'administrateur ou gérant pour sociétés suisses détenues par des actionnaires étrangers. Fiduciaire Bleich, Genève, depuis 2004.",
                canonical: "https://fiduciaire-bleich.ch/administrateur-gerant",
            }}
            jsonLd={{ service: serviceJsonLd, breadcrumb: breadcrumbJsonLd, faq: faqJsonLd }}
            breadcrumb="Administrateur et gérant"
            hero={{
                eyebrow: "Nos services",
                h1Prefix: "Administrateur et gérant à",
                h1Italic: "Genève.",
                subtitle: "Une représentation locale fiable pour votre société suisse.",
                intro:
                    "Le droit suisse exige que certaines sociétés disposent d'un administrateur ou d'un gérant domicilié en Suisse. Fiduciaire Bleich met à disposition des personnes qualifiées pour assurer cette représentation, dans le respect total des obligations légales et des intérêts de vos actionnaires.",
            }}
            prestations={prestations}
            prestationsHeading={
                <>
                    Ce que <span className="italic">nous faisons.</span>
                </>
            }
            etapes={etapes}
            processHeading={
                <>
                    Un mandat clair et <span className="italic">encadré.</span>
                </>
            }
            pourquoiHeading={
                <>
                    Une représentation
                    <br />
                    <span className="italic">sérieuse et engagée.</span>
                </>
            }
            pourquoiCols={pourquoiCols}
            faqHeading={
                <>
                    Questions fréquentes sur
                    <br />
                    l'<span className="italic">administrateur et le gérant.</span>
                </>
            }
            faqs={faqs}
            cta={{
                heading: (
                    <>
                        Parlons de votre <span className="italic">société.</span>
                    </>
                ),
                text: "Un premier entretien gratuit et confidentiel permet d'examiner vos besoins de représentation locale et la manière dont Fiduciaire Bleich peut y répondre. Sans engagement.",
            }}
        />
    );
};

export default AdministrateurGerant;
